import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
  InputAdornment,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SearchIcon from '@mui/icons-material/Search';
import { complianceApi, type CompliancePreviewResponse, type CompliancePreviewRequest } from '../lib/api';
import StatesPicker from './lookup/StatesPicker';
import ChoiceList from './lookup/ChoiceList';
import ResultsView from './lookup/ResultsView';
import {
  ASSETS_OPTIONS,
  EMPLOYEE_OPTIONS,
  ENTITY_OPTIONS,
  FILING_STATUS_OPTIONS,
  FILING_TYPE_OPTIONS,
  FOUNDATION_STATUS_OPTIONS,
  FUNDRAISING_METHOD_OPTIONS,
  INITIAL_RESPONSES,
  REVENUE_OPTIONS,
  STEPS,
  YES_NO_OPTIONS,
  type AssessmentResponses,
  type FilingStatusValue,
  type StepId,
} from './lookup/data';

function isStepAnswered(stepId: StepId, r: AssessmentResponses): boolean {
  switch (stepId) {
    case 'states':
      return r.states.length > 0;
    case 'fundraisingStates':
      return r.fundraisingStates.length > 0;
    case 'entity':
      if (!r.entity) return false;
      if (r.entity === '501c3' && !r.isChurch) return false;
      // Every 501(c)(3) is one or the other, and the answer decides whether the
      // annual return is the 990 series or the 990-PF. Churches are exempt from
      // the 990 entirely, so the distinction does not apply to them.
      if (r.entity === '501c3' && r.isChurch === 'no' && !r.foundationStatus) return false;
      return true;
    case 'revenue':
      return !!r.revenue;
    case 'assets':
      return !!r.assets;
    case 'fundraising':
      // optional
      return true;
    case 'usesProfessionalFundraiser':
      return !!r.usesProfessionalFundraiser;
    case 'conductRaffles':
      return !!r.conductRaffles;
    case 'hasEmployees':
      return r.hasEmployees.length > 0;
    case 'filingStatus':
      // Standing and filing type both default sensibly when unanswered, so the
      // step never blocks the lookup.
      return true;
  }
}

export default function ComplianceLookup() {
  const [step, setStep] = useState(0);
  const [responses, setResponses] = useState<AssessmentResponses>(INITIAL_RESPONSES);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [report, setReport] = useState<CompliancePreviewResponse | null>(null);
  const [explained, setExplained] = useState(false);

  const buildPayload = (): CompliancePreviewRequest => ({
    entity: responses.entity,
    states: responses.states,
    fundraisingStates: responses.fundraisingStates,
    revenue: responses.revenue,
    assets: responses.assets,
    fundraising: responses.fundraising,
    hasEmployees: responses.hasEmployees,
    isChurch: responses.isChurch,
    usesProfessionalFundraiser: responses.usesProfessionalFundraiser || 'no',
    conductRaffles: responses.conductRaffles || 'no',
    foundationStatus: responses.foundationStatus || null,
    filingType: responses.filingType || null,
    domesticState: responses.domesticState || null,
    annualRevenueAmount: responses.annualRevenueAmount
      ? Number(responses.annualRevenueAmount)
      : null,
    // Omit agencies left unanswered rather than sending 'current', so the API
    // can tell "confirmed in good standing" from "not asked".
    filingStatus:
      responses.solicitationStatus || responses.annualReportStatus || responses.federalStatus
        ? {
            ...(responses.solicitationStatus
              ? { charitableSolicitation: responses.solicitationStatus }
              : {}),
            ...(responses.annualReportStatus ? { annualReport: responses.annualReportStatus } : {}),
            ...(responses.federalStatus ? { federal: responses.federalStatus } : {}),
          }
        : null,
  });

  const handleExplain = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await complianceApi.preview(buildPayload(), { explain: true });
      setReport(res);
      setExplained(true);
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } }; message?: string };
      setError(err.response?.data?.error ?? err.message ?? 'Explain failed');
    } finally {
      setLoading(false);
    }
  };

  const handleExportCsv = () => {
    if (!report) return;
    const headers = ['category', 'priority', 'state', 'title', 'deadline', 'fee', 'description'];
    const escape = (s: string) => (/[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s);
    const rows = report.filings.map((f) =>
      headers
        .map((h) => escape(String((f as unknown as Record<string, unknown>)[h] ?? '')))
        .join(',')
    );
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `compliance-${responses.entity || 'report'}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const current = STEPS[step];
  const canProceed = useMemo(() => isStepAnswered(current.id, responses), [current.id, responses]);
  const isLast = step === STEPS.length - 1;

  const update = <K extends keyof AssessmentResponses>(key: K, value: AssessmentResponses[K]) => {
    setResponses((prev) => {
      const next = { ...prev, [key]: value };
      if (key === 'entity') {
        next.isChurch = null;
        next.foundationStatus = '';
      }
      // A church files no 990, so the public-charity distinction is moot.
      if (key === 'isChurch' && value === 'yes') next.foundationStatus = '';
      return next;
    });
  };

  const handleNext = async () => {
    if (!isLast) {
      setStep((s) => s + 1);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await complianceApi.preview(buildPayload());
      setReport(res);
      setExplained(false);
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } }; message?: string };
      setError(err.response?.data?.error ?? err.message ?? 'Lookup failed');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setResponses(INITIAL_RESPONSES);
    setStep(0);
    setReport(null);
    setError('');
    setExplained(false);
  };

  if (report) {
    return (
      <ResultsView
        report={report}
        explained={explained}
        onRestart={handleReset}
        onExplain={handleExplain}
        onExportCsv={handleExportCsv}
      />
    );
  }

  return (
    <Box>
      <Box mb={3}>
        <Typography variant="h5" fontWeight={700}>
          Compliance Lookup
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Walk through the assessment to surface federal, state, and fundraising requirements.
        </Typography>
      </Box>

      <Stepper activeStep={step} alternativeLabel sx={{ mb: 4 }}>
        {STEPS.map((s, i) => (
          <Step key={s.id} completed={i < step}>
            <StepLabel>{s.shortLabel}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Paper variant="outlined" sx={{ p: { xs: 2, sm: 4 }, mb: 3 }}>
        <Typography variant="h6" fontWeight={700} gutterBottom>
          {current.title}
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          {current.subtitle}
        </Typography>

        {current.id === 'states' && (
          <Stack spacing={3}>
            <StatesPicker value={responses.states} onChange={(v) => update('states', v)} />
            {responses.states.length > 0 && (
              <Box>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                  Which of those is the state of incorporation?
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  Operating outside the state of incorporation generally requires a certificate of
                  authority in each other state. Optional — leave blank if unknown.
                </Typography>
                <ChoiceList
                  options={responses.states.map((st) => ({ value: st, label: st }))}
                  value={responses.domesticState}
                  onChange={(v) => update('domesticState', v === responses.domesticState ? '' : v)}
                />
              </Box>
            )}
          </Stack>
        )}

        {current.id === 'fundraisingStates' && (
          <StatesPicker
            value={responses.fundraisingStates}
            onChange={(v) => update('fundraisingStates', v)}
          />
        )}

        {current.id === 'entity' && (
          <Stack spacing={3}>
            <ChoiceList
              options={ENTITY_OPTIONS}
              value={responses.entity}
              onChange={(v) => update('entity', v)}
            />
            {responses.entity === '501c3' && (
              <Box>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                  Is the organization a church or religious organization?
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  Churches are exempt from filing Form 990 and follow different state rules.
                </Typography>
                <ChoiceList
                  options={YES_NO_OPTIONS}
                  value={responses.isChurch ?? ''}
                  onChange={(v) => update('isChurch', v as 'yes' | 'no')}
                />
              </Box>
            )}
            {responses.entity === '501c3' && responses.isChurch === 'no' && (
              <Box>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                  Is it a public charity or a private foundation?
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  Every 501(c)(3) is one or the other, and the filing rules differ. A private
                  foundation files the Form 990-PF every year regardless of size.
                </Typography>
                <ChoiceList
                  options={FOUNDATION_STATUS_OPTIONS}
                  value={responses.foundationStatus}
                  onChange={(v) => update('foundationStatus', v as 'publicCharity' | 'privateFoundation')}
                />
              </Box>
            )}
          </Stack>
        )}

        {current.id === 'revenue' && (
          <Stack spacing={3}>
            <ChoiceList
              options={REVENUE_OPTIONS}
              value={responses.revenue}
              onChange={(v) => update('revenue', v)}
            />
            <Box>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                Exact annual gross revenue
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Optional, but state audit thresholds commonly sit at $500,000, $750,000, and
                $1,000,000 — the bands above cannot tell those apart. Without an exact figure every
                applicable threshold is reported as needing confirmation.
              </Typography>
              <TextField
                size="small"
                type="number"
                placeholder="e.g. 750000"
                value={responses.annualRevenueAmount}
                onChange={(e) => update('annualRevenueAmount', e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                sx={{ maxWidth: 260 }}
              />
            </Box>
          </Stack>
        )}

        {current.id === 'assets' && (
          <ChoiceList
            options={ASSETS_OPTIONS}
            value={responses.assets}
            onChange={(v) => update('assets', v)}
          />
        )}

        {current.id === 'fundraising' && (
          <ChoiceList
            multiple
            options={FUNDRAISING_METHOD_OPTIONS}
            value={responses.fundraising}
            onChange={(v) => update('fundraising', v)}
          />
        )}

        {current.id === 'usesProfessionalFundraiser' && (
          <ChoiceList
            options={YES_NO_OPTIONS}
            value={responses.usesProfessionalFundraiser}
            onChange={(v) => update('usesProfessionalFundraiser', v as 'yes' | 'no')}
          />
        )}

        {current.id === 'conductRaffles' && (
          <ChoiceList
            options={YES_NO_OPTIONS}
            value={responses.conductRaffles}
            onChange={(v) => update('conductRaffles', v as 'yes' | 'no')}
          />
        )}

        {current.id === 'hasEmployees' && (
          <ChoiceList
            multiple
            options={EMPLOYEE_OPTIONS}
            value={responses.hasEmployees}
            onChange={(v) => update('hasEmployees', v)}
          />
        )}

        {current.id === 'filingStatus' && (
          <Stack spacing={4}>
            <Box>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                Is this an initial filing or a renewal?
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Many states time the first annual report or registration off incorporation rather
                than the fiscal year. Defaults to renewal.
              </Typography>
              <ChoiceList
                options={FILING_TYPE_OPTIONS}
                value={responses.filingType}
                onChange={(v) => update('filingType', v as 'initial' | 'renewal')}
              />
            </Box>

            {(
              [
                ['solicitationStatus', 'State Charitable Solicitation'],
                ['annualReportStatus', 'State Annual Report'],
                ['federalStatus', 'Federal Filings'],
              ] as const
            ).map(([key, label]) => (
              <Box key={key}>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                  {label} status
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  Leave blank if unknown — unanswered agencies are treated as current.
                </Typography>
                <ChoiceList
                  options={FILING_STATUS_OPTIONS}
                  value={responses[key]}
                  onChange={(v) =>
                    update(key, (v === responses[key] ? '' : v) as FilingStatusValue)
                  }
                />
              </Box>
            ))}
          </Stack>
        )}
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Button
          startIcon={<ArrowBackIcon />}
          disabled={step === 0 || loading}
          onClick={() => setStep((s) => s - 1)}
        >
          Back
        </Button>

        <Stack direction="row" spacing={1.5} alignItems="center">
          {step > 0 && (
            <Button
              size="small"
              startIcon={<RestartAltIcon />}
              onClick={handleReset}
              disabled={loading}
            >
              Start over
            </Button>
          )}
          <Button
            variant="contained"
            endIcon={
              loading ? (
                <CircularProgress size={18} color="inherit" />
              ) : isLast ? (
                <SearchIcon />
              ) : (
                <ArrowForwardIcon />
              )
            }
            disabled={!canProceed || loading}
            onClick={handleNext}
          >
            {isLast ? (loading ? 'Looking up…' : 'Run lookup') : 'Continue'}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}
